import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { Button, CopyIcon, IconButton } from "../../components/ui";
import { copyText } from "../../lib/clipboard";
import {
  modelSummaryContent,
  useAgentDocumentsByIds,
  type XLMModel,
} from "../api/e2e";
import type { ModelReference } from "../lib/modelReferences";
import { citedDocumentIds } from "../lib/agentSummary";
import { useAgentDocumentOpener } from "./AgentDocumentActionContext";
import AgentSummaryDocumentPreview from "./AgentSummaryDocumentPreview";
import AgentSummaryMarkdown from "./AgentSummaryMarkdown";

type SummaryState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; content: string }
  | { kind: "error"; message: string };

export default function AgentSummaryPanel({
  companyId,
  runId,
  versionId,
  model,
  modelReferenceState,
  onOpenModelReference,
}: {
  companyId: string;
  runId: string;
  versionId: string;
  model: XLMModel | undefined;
  modelReferenceState: "loading" | "ready" | "error";
  onOpenModelReference: (reference: ModelReference) => void;
}) {
  const [state, setState] = useState<SummaryState>({ kind: "idle" });
  const [copying, setCopying] = useState(false);
  const {
    closePreview,
    openDocument,
    openingDocumentId: hostOpeningDocumentId,
    previewDocument,
  } = useAgentDocumentOpener();
  const activeContent = state.kind === "loaded" ? state.content : null;
  const citedIds =
    activeContent === null ? [] : citedDocumentIds(activeContent);
  const documents = useAgentDocumentsByIds(companyId, citedIds);
  const runDocuments = (documents.data ?? []).filter((document) =>
    document.run_ids.includes(runId),
  );
  const referenceState =
    activeContent === null
      ? "loading"
      : documents.error
        ? "error"
        : citedIds.length > 0 && documents.isPending
          ? "loading"
          : "ready";

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const response = await modelSummaryContent(companyId, versionId);
      const content = await response.text();
      setState({ kind: "loaded", content });
    } catch (error) {
      setState({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "The summary could not be loaded.",
      });
    }
  }, [companyId, versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="agent-summary-panel">
      {(state.kind === "idle" || state.kind === "loading") && (
        <p role="status">Loading model summary…</p>
      )}
      {state.kind === "error" && (
        <div className="agent-summary-panel__error">
          <p>{state.message}</p>
          <Button tone="quiet" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      )}
      {documents.error && activeContent !== null && (
        <div className="agent-summary-panel__reference-error" role="alert">
          <span>Document references could not be loaded.</span>
          <Button tone="quiet" size="sm" onClick={() => documents.refetch()}>
            Retry references
          </Button>
        </div>
      )}
      {activeContent !== null && (
        <div className="agent-summary-layout">
          <div className="agent-summary-layout__main">
            <IconButton
              tone="quiet"
              containerClassName="agent-summary-layout__copy"
              label="Copy summary"
              tooltip="Copy the model summary to your clipboard."
              icon={<CopyIcon />}
              loading={copying}
              onClick={() => {
                setCopying(true);
                void copyText(activeContent)
                  .then(() => toast.success("Summary copied."))
                  .catch((error: unknown) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : "The content could not be copied.",
                    ),
                  )
                  .finally(() => setCopying(false));
              }}
            />
            <AgentSummaryMarkdown
              source={activeContent}
              documents={runDocuments}
              referenceState={referenceState}
              model={model}
              modelReferenceState={modelReferenceState}
              openingDocumentId={
                hostOpeningDocumentId ?? previewDocument?.id ?? null
              }
              onOpenDocument={openDocument}
              onOpenModelReference={onOpenModelReference}
            />
          </div>
        </div>
      )}
      {previewDocument && (
        <AgentSummaryDocumentPreview
          companyId={companyId}
          document={previewDocument}
          onClose={closePreview}
        />
      )}
    </section>
  );
}
